import React from 'react';
import { i18n } from 'config/locales';
import ValidationElement from 'components/Form/ValidationElement';
import { ValidationElementProps } from 'components/Form/ValidationElement/ValidationElement';
import {
  CommonOnUpdate, CommonOnError, CommonScrollIntoView, CommonRequired
} from 'types/props';
import { ErrorCode } from 'redux/modules/application/reducers/error';
import { LegalInvestigationsSuspendedItemModel } from 'forms/sections/legal/investigation/issues/types';
import Field from 'components/Form/Field';
import NotApplicable from 'components/Form/NotApplicable';
import { LegalInvestigationsInvestigatingAgencyModel } from 'forms/sections/legal/investigation/history/types';
import DateControl from 'components/Form/DateControl';
import Textarea from 'components/Form/Textarea';
import { LegalInvestigationsIssueOutcomeModel } from 'forms/sections/legal/investigation/issues/types';
import { LEGAL_INVESTIGATIONS_CLEARANCE_ISSUES, LEGAL_INVESTIGATIONS_PIV_ISSUES } from 'config/formSections/legal';
import { isPVQForm } from 'helpers/form';
import IssueOutcome from './IssueOutcome';
import InvestigatingAgency from './InvestigatingAgency';

interface Props extends Partial<LegalInvestigationsSuspendedItemModel>, ValidationElementProps {
  onUpdate?: CommonOnUpdate
  onError?: CommonOnError
  required?: CommonRequired
  scrollIntoView?: CommonScrollIntoView
  formType?: string
  prefix: string
  showReinstatedDateWhenReinstated?: boolean
}

export default class SuspendedItem extends ValidationElement<Props> {
  update = (queue: Partial<LegalInvestigationsSuspendedItemModel>) => {
    this.props.onUpdate({
      Agency: this.props.Agency,
      AgencyExplanation: this.props.AgencyExplanation,
      AgencyNotApplicable: this.props.AgencyNotApplicable,
      Date: this.props.Date,
      DateNotApplicable: this.props.DateNotApplicable,
      Outcome: this.props.Outcome,
      OutcomeExplanation: this.props.OutcomeExplanation,
      Explanation: this.props.Explanation,
      ReinstatedDate: this.props.ReinstatedDate,
      ReinstatedDateNotApplicable: this.props.ReinstatedDateNotApplicable,
      ...queue,
    });
  };

  updateAgency = (values: Partial<LegalInvestigationsSuspendedItemModel>) => {
    this.update({
      Agency: values.Agency,
      AgencyExplanation: values.Agency?.value === 'Other' ? this.props.AgencyExplanation : {} as LegalInvestigationsSuspendedItemModel['AgencyExplanation'],
    });
  };

  updateAgencyExplanation = (value: LegalInvestigationsSuspendedItemModel['AgencyExplanation']) => {
    this.update({
      AgencyExplanation: value
    });
  };

  updateAgencyNotApplicable = (
    values: LegalInvestigationsSuspendedItemModel['AgencyNotApplicable']
  ) => {
    this.update({
      AgencyNotApplicable: values,
      Agency: {} as LegalInvestigationsSuspendedItemModel['Agency'],
      AgencyExplanation: {} as LegalInvestigationsSuspendedItemModel['AgencyExplanation'],
    });
  };

  updateDate = (values: LegalInvestigationsSuspendedItemModel['Date']) => {
    this.update({
      Date: values,
    });
  };

  updateDateNotApplicable = (
    values: LegalInvestigationsSuspendedItemModel['DateNotApplicable']
  ) => {
    this.update({
      DateNotApplicable: values,
      Date: {}
    });
  };

  updateExplanation = (values: LegalInvestigationsSuspendedItemModel['Explanation']) => {
    this.update({
      Explanation: values,
    });
  };

  updateOutcome = (values: LegalInvestigationsIssueOutcomeModel) => {
    this.update({
      Outcome: values.Outcome,
      OutcomeExplanation: values.OutcomeExplanation,
      ReinstatedDateNotApplicable: { applicable: true },
    });
  };

  updateReinstatedDate = (values: LegalInvestigationsSuspendedItemModel['ReinstatedDate']) => {
    this.update({
      ReinstatedDate: values,
    });
  };

  updateReinstatedDateNotApplicable = (
    values: LegalInvestigationsSuspendedItemModel['ReinstatedDateNotApplicable']
  ) => {
    this.update({
      ReinstatedDateNotApplicable: values,
      ReinstatedDate: {}
    });
  };

  render() {
    const attribute = `SuspensionList.items[${this.props.$index}].Item`;
    const isPVQ = isPVQForm(this.props.formType);

    return isPVQ ? (
      <div>

        <Field
          title={i18n.t(`${this.props.prefix}.suspended.heading.agency`)}
          adjustFor="big-buttons"
          scrollIntoView={this.props.scrollIntoView}
          attribute={`${attribute}.Agency`}
          exactAttribute
        >
          <InvestigatingAgency
            {...{
              Agency: this.props.Agency,
              Explanation: this.props.AgencyExplanation,
            }}
            name="Agency"
            onUpdate={this.updateAgency}
            onError={this.props.onError}
            className="legal-investigations-history-agency"
            required={this.props.required && this.props.AgencyNotApplicable?.applicable}
            scrollIntoView={this.props.scrollIntoView}
            attribute={attribute}
            formType={this.props.formType}
          />
        </Field>

        {this.props.Agency?.value === 'Other'
          && (
          <Field
            title={i18n.t('legal.pvq.investigations.history.heading.agencyexplanation')}
            scrollIntoView={this.props.scrollIntoView}
            attribute={`${attribute}.AgencyExplanation`}
            exactAttribute
          >
            <Textarea
              {...this.props.AgencyExplanation}
              name="AgencyExplanation"
              className="legal-investigations-suspended-agency-explanation"
              onUpdate={this.updateAgencyExplanation}
              onError={this.props.onError}
              required={this.props.required}
              disabled={this.props.disabled}
              attribute={`${this.props.attribute}.AgencyExplanation`}
            />
          </Field>
          )}

        <Field
          title={i18n.t(`${this.props.prefix}.suspended.heading.date`)}
          help="legal.pvq.investigations.clearanceissues.suspended.help.date"
          adjustFor="datecontrol"
          scrollIntoView={this.props.scrollIntoView}
          attribute={`${attribute}.Date`}
        >
          <NotApplicable
            {...this.props.DateNotApplicable}
            name="DateNotApplicable"
            className="legal-investigations-suspended-date-notapplicable"
            onUpdate={this.updateDateNotApplicable}
            onError={this.props.onError}
            or={i18n.m(`${this.props.prefix}.suspended.para.or`)}
            label={i18n.t(`${this.props.prefix}.suspended.label.idk`)}
            required={this.props.required}
            attribute={`${attribute}.DateNotApplicable`}
            includeNAConfirmation={Boolean(this.props.Date)}
          >
            <DateControl
              {...this.props.Date}
              name="Date"
              onUpdate={this.updateDate}
              minDateEqualTo
              onError={this.props.onError}
              className="legal-investigations-suspended-date"
              required={this.props.required}
              hideDay
              attribute={`${attribute}.Date`}
            />
          </NotApplicable>
        </Field>

        <Field
          title={i18n.t(`${this.props.prefix}.suspended.heading.reason`)}
          help={`${this.props.prefix}.suspended.help.explanation`}
          scrollIntoView={this.props.scrollIntoView}
          attribute={`${attribute}.Explanation`}
        >
          <Textarea
            {...this.props.Explanation}
            name="Explanation"
            className="legal-investigations-history-clearanceissues-revoked-explanation"
            onUpdate={this.updateExplanation}
            onError={this.props.onError}
            required={this.props.required}
            attribute={`${attribute}.Explanation`}
          />
        </Field>

        <Field
          title={i18n.t(`${this.props.prefix}.suspended.heading.outcome`)}
          adjustFor="big-buttons"
          scrollIntoView={this.props.scrollIntoView}
          attribute={`${attribute}.Outcome`}
        >
          <IssueOutcome
            {...{
              Outcome: this.props.Outcome,
              OutcomeExplanation: this.props.OutcomeExplanation,
            }}
            name="Outcome"
            onUpdate={this.updateOutcome}
            onError={this.props.onError}
            className="legal-investigations-suspended-outcome"
            required={this.props.required}
            scrollIntoView={this.props.scrollIntoView}
            attribute={attribute}
          />
        </Field>

        {(this.props.showReinstatedDateWhenReinstated && this.props.Outcome?.value === 'Reinstated')
          && (
            <Field
              title={i18n.t(`${this.props.prefix}.suspended.heading.reinstatedDate`)}
              help={i18n.t(`${this.props.prefix}.suspended.help.reinstateddate`)}
              adjustFor="datecontrol"
              scrollIntoView={this.props.scrollIntoView}
              attribute={`${attribute}.ReinstatedDate`}
            >
              <NotApplicable
                {...this.props.ReinstatedDateNotApplicable}
                name="ReinstatedDateNotApplicable"
                className="legal-investigations-suspended-reinstateddate-notapplicable"
                onUpdate={this.updateReinstatedDateNotApplicable}
                onError={this.props.onError}
                or={i18n.m(`${this.props.prefix}.suspended.para.or`)}
                label={i18n.t(`${this.props.prefix}.suspended.label.idk`)}
                required={this.props.required}
                attribute={`${attribute}.ReinstatedDateNotApplicable`}
                includeNAConfirmation={Boolean(this.props.ReinstatedDate)}
              >
                <DateControl
                  {...this.props.ReinstatedDate}
                  name="ReinstatedDate"
                  onUpdate={this.updateReinstatedDate}
                  minDateEqualTo
                  onError={this.props.onError}
                  className="legal-investigations-suspended-reinstateddate"
                  required={this.props.required}
                  hideDay
                  attribute={`${attribute}.ReinstatedDate`}
                />
              </NotApplicable>
            </Field>
          )}
      </div>
    ) : (
      <div>

        <Field
          title={i18n.t(`${this.props.prefix}.suspended.heading.agency`)}
          adjustFor="big-buttons"
          scrollIntoView={this.props.scrollIntoView}
          attribute={`${attribute}.Agency`}
          exactAttribute
        >
          <NotApplicable
            {...this.props.AgencyNotApplicable}
            name="AgencyNotApplicable"
            className="legal-investigations-suspended-agency-notapplicable"
            onUpdate={this.updateAgencyNotApplicable}
            onError={this.props.onError}
            or={i18n.m(`${this.props.prefix}.suspended.para.or`)}
            label={i18n.t(`${this.props.prefix}.suspended.label.idk`)}
            required={this.props.required}
            attribute={`${attribute}.AgencyNotApplicable`}
            includeNAConfirmation={Boolean(this.props.Agency?.value || this.props.AgencyExplanation?.value)}
          >
            <InvestigatingAgency
              {...{
                Agency: this.props.Agency,
                Explanation: this.props.AgencyExplanation,
              }}
              name="Agency"
              onUpdate={this.updateAgency}
              onError={this.props.onError}
              className="legal-investigations-history-agency"
              required={this.props.required && this.props.AgencyNotApplicable?.applicable}
              scrollIntoView={this.props.scrollIntoView}
              attribute={attribute}
              formType={this.props.formType}
            />
          </NotApplicable>
        </Field>

        {this.props.Agency?.value === 'Other'
          && (
          <Field
            title={i18n.t('legal.pvq.investigations.history.heading.agencyexplanation')}
            scrollIntoView={this.props.scrollIntoView}
            attribute={`${attribute}.AgencyExplanation`}
            exactAttribute
          >
            <Textarea
              {...this.props.AgencyExplanation}
              name="AgencyExplanation"
              className="legal-investigations-suspended-agency-explanation"
              onUpdate={this.updateAgencyExplanation}
              onError={this.props.onError}
              required={this.props.required}
              disabled={this.props.disabled}
              attribute={`${this.props.attribute}.AgencyExplanation`}
            />
          </Field>
          )}

        <Field
          title={i18n.t(`${this.props.prefix}.suspended.heading.date`)}
          help="legal.pvq.investigations.clearanceissues.suspended.help.date"
          adjustFor="datecontrol"
          scrollIntoView={this.props.scrollIntoView}
          attribute={`${attribute}.Date`}
        >
          <NotApplicable
            {...this.props.DateNotApplicable}
            name="DateNotApplicable"
            className="legal-investigations-suspended-date-notapplicable"
            onUpdate={this.updateDateNotApplicable}
            onError={this.props.onError}
            or={i18n.m(`${this.props.prefix}.suspended.para.or`)}
            label={i18n.t(`${this.props.prefix}.suspended.label.idk`)}
            required={this.props.required}
            attribute={`${attribute}.DateNotApplicable`}
            includeNAConfirmation={Boolean(this.props.Date)}
          >
            <DateControl
              {...this.props.Date}
              name="Date"
              onUpdate={this.updateDate}
              minDateEqualTo
              onError={this.props.onError}
              className="legal-investigations-suspended-date"
              required={this.props.required}
              hideDay
              attribute={`${attribute}.Date`}
            />
          </NotApplicable>
        </Field>

        <Field
          title={i18n.t(`${this.props.prefix}.suspended.heading.reason`)}
          help={`${this.props.prefix}.suspended.help.explanation`}
          scrollIntoView={this.props.scrollIntoView}
          attribute={`${attribute}.Explanation`}
        >
          <Textarea
            {...this.props.Explanation}
            name="Explanation"
            className="legal-investigations-history-clearanceissues-revoked-explanation"
            onUpdate={this.updateExplanation}
            onError={this.props.onError}
            required={this.props.required}
            attribute={`${attribute}.Explanation`}
          />
        </Field>

        <Field
          title={i18n.t(`${this.props.prefix}.suspended.heading.outcome`)}
          adjustFor="big-buttons"
          scrollIntoView={this.props.scrollIntoView}
          attribute={`${attribute}.Outcome`}
        >
          <IssueOutcome
            {...{
              Outcome: this.props.Outcome,
              OutcomeExplanation: this.props.OutcomeExplanation,
            }}
            name="Outcome"
            onUpdate={this.updateOutcome}
            onError={this.props.onError}
            className="legal-investigations-suspended-outcome"
            required={this.props.required}
            scrollIntoView={this.props.scrollIntoView}
            attribute={attribute}
          />
        </Field>

        {(this.props.showReinstatedDateWhenReinstated && this.props.Outcome?.value === 'Reinstated')
          && (
            <Field
              title={i18n.t(`${this.props.prefix}.suspended.heading.reinstatedDate`)}
              help={i18n.t(`${this.props.prefix}.suspended.help.reinstateddate`)}
              adjustFor="datecontrol"
              scrollIntoView={this.props.scrollIntoView}
              attribute={`${attribute}.ReinstatedDate`}
            >
              <NotApplicable
                {...this.props.ReinstatedDateNotApplicable}
                name="ReinstatedDateNotApplicable"
                className="legal-investigations-suspended-reinstateddate-notapplicable"
                onUpdate={this.updateReinstatedDateNotApplicable}
                onError={this.props.onError}
                or={i18n.m(`${this.props.prefix}.suspended.para.or`)}
                label={i18n.t(`${this.props.prefix}.suspended.label.idk`)}
                required={this.props.required}
                attribute={`${attribute}.ReinstatedDateNotApplicable`}
                includeNAConfirmation={Boolean(this.props.ReinstatedDate)}
              >
                <DateControl
                  {...this.props.ReinstatedDate}
                  name="ReinstatedDate"
                  onUpdate={this.updateReinstatedDate}
                  minDateEqualTo
                  onError={this.props.onError}
                  className="legal-investigations-suspended-reinstateddate"
                  required={this.props.required}
                  hideDay
                  attribute={`${attribute}.ReinstatedDate`}
                />
              </NotApplicable>
            </Field>
          )}
      </div>
    );
  }
}

SuspendedItem.defaultProps = {
  onUpdate: (queue) => { },
  onError: (value, arr: ErrorCode[]) => arr,
  showReinstatedDateWhenReinstated: false,
  Agency: {},
  AgencyExplanation: {},
  AgencyNotApplicable: { applicable: true },
  Date: {},
  DateNotApplicable: { applicable: true },
  Explanation: {},
};
